// Public how-it-works page (no auth gate). Long-form explanation of what
// Inkling is, who it's for, and how the UX feels in practice.
//
// Linked from Landing + footer. Inclusive language throughout — never
// assumes a specific household shape.

import { Link } from "react-router-dom";

export function HowItWorks() {
  return (
    <main className="min-h-screen bg-stone-50">
      <header className="px-6 py-10 border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-3xl">
          <Link to="/" className="text-sm text-stone-500 hover:text-stone-900">← back to inkling.family</Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">How Inkling works</h1>
          <p className="mt-3 text-stone-600">
            A long-form walkthrough of the product, the language we use about household
            configurations, and the parts that are still being built.
          </p>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 py-10 prose prose-stone">
        <Section title="The problem we're solving">
          <figure className="not-prose my-6">
            <picture>
              <source srcSet="/inkling-chaos.webp" type="image/webp" />
              <img
                src="/inkling-chaos.jpg"
                alt="A close-up of overlapping family-coordination signals on a worn surface — a phone screen with stacked unread text bubbles, a yellow sticky note half-falling off, a torn corner of a school flyer with a crossed-out date, a calendar page with events marked then crossed out, a dropped pen — the chaos before the calendar gets organized."
                className="w-full h-auto rounded-2xl ring-1 ring-stone-200/70"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </figure>
          <p>
            Family coordination breaks down at the input layer. Plans get communicated
            verbally, in side-channel texts, in iMessage threads, in photos of school flyers.
            By the time the day arrives, half the household doesn't know what's happening.
            Existing calendar apps technically solve this, but they all require the same thing:
            one person, every time, opens the app, types a structured event, fills in the
            fields. That person quits within a week.
          </p>
          <p>
            Inkling sits on top of how people <em>already</em> communicate. You text events to
            a phone number. We do the structured-data part for you.
          </p>
        </Section>

        <Section title="A note on the word ‘family’">
          <figure className="not-prose my-6">
            <picture>
              <source srcSet="/inkling-family-means.webp" type="image/webp" />
              <img
                src="/inkling-family-means.jpg"
                alt="A horizontal ribbon of four blended vignettes — adult hands holding hands with a child's smaller hand reaching up; two adult hands of different skin tones gesturing over a shared phone; an older hand resting on a younger hand cradling a coffee mug; three hands meeting in greeting — flowing into each other through soft watercolor washes."
                className="w-full h-auto rounded-2xl ring-1 ring-stone-200/70"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </figure>
          <p>
            We use the word "family" a lot — it's in the name. We don't mean any specific
            household shape by it. <strong>Family means whatever it means to you.</strong>
          </p>
          <p>
            Inkling has been used (or is being designed for) all of these:
          </p>
          <ul>
            <li>Two-parent households with kids</li>
            <li>Single parents, with or without co-parents</li>
            <li>Co-parenting across two physical households (each home gets its own Inkling family, or one shared family across both — your call)</li>
            <li>Couples and partners without kids</li>
            <li>Multi-generational households (grandparents, parents, kids together)</li>
            <li>Found family and chosen family</li>
            <li>Roommate groups who coordinate logistics</li>
            <li>Friend groups who trade kid pickups, run errands together, share a Costco card</li>
            <li>Adult siblings coordinating care for an aging parent</li>
            <li>Any other configuration of people who plan things together</li>
          </ul>
          <p>
            If you coordinate plans together, you're a family for our purposes. We don't ask
            how you're related. We don't ask anyone's gender, marital status, or last name.
          </p>
        </Section>

        <Section title="Setting up — three paths">
          <h3>Text to start (fastest, no email)</h3>
          <p>
            Text "Hi" (or anything) to <strong>(877) 873-1681</strong>. We'll ask for your
            family's name, then your name, then confirm — three short replies and your family
            exists. You can text your first event immediately after. An email is never required;
            we'll ask once, at the end, only if you want a sign-in link for the web calendar
            (you can reply "skip").
          </p>
          <h3>Start a new family on the web</h3>
          <p>
            Go to <Link to="/">inkling.family</Link>, enter your email, click the sign-in link.
            On the onboarding screen pick "Start a new family." Give your family a name (defaults
            to your first name + "family" — change it to whatever feels right), pick your home
            location (used to resolve "school" and "soccer practice" to your city's timezone),
            and add your phone number. We provision your family's phone number and you're done
            — text it your first event right then.
          </p>
          <h3>Join an existing family</h3>
          <p>
            If someone else has already set up the family, pick "Join an existing family"
            instead. Give us their email or phone number; Inkling routes a join request to them
            (via email if they have one, otherwise SMS). They approve it on their Members page
            and you're in.
          </p>
        </Section>

        <Section title="Adding more people">
          <p>
            Once you're in a family, the Members page lets you add others. You can choose:
          </p>
          <ul>
            <li>
              <strong>Send via SMS:</strong> the new person gets a text from the family number
              asking them to reply YES. Once they do, they can text events in immediately.
            </li>
            <li>
              <strong>Send via email:</strong> the new person gets a friendly email with an
              "Accept" link. Click it and they're added (the link is good for 7 days). Phone
              number is still required so they can text events in once added.
            </li>
          </ul>
          <p>
            Admins can also <strong>resend invites</strong> if a new member misses the first
            one, and <strong>remove members</strong> if someone shouldn't be on the family
            calendar anymore. The admin role itself can't be removed (that would orphan the
            family) — admin handover is a separate manual step.
          </p>
        </Section>

        <Section title="What you can text in">
          <ul>
            <li>
              <strong>Plain text.</strong> "Dentist Wednesday at 3:15 downtown." This is
              the most common case. We pull out the time, place, and (if you mentioned them)
              the people involved.
            </li>
            <li>
              <strong>Photos and screenshots.</strong> A photo of a school flyer, a swim-meet
              schedule, a class email. We read the image. Flyers with multiple events get all
              of them pulled out at once with a single confirmation listing what was added.
            </li>
            <li>
              <strong>Voice notes.</strong> Hold the microphone in Messages, send the voice
              note as audio. We transcribe and parse in one pass.
            </li>
            <li>
              <strong>Inline corrections.</strong> Reply to a confirmation within ten minutes
              with something like "no that's at 5pm not 4" and we update the event you just
              added.
            </li>
          </ul>
        </Section>

        <Section title="How confident we are, and what we do when we're not">
          <p>
            Every parse comes with a confidence score. We act differently depending on it:
          </p>
          <ul>
            <li>
              <strong>High confidence (most cases).</strong> Event goes on the calendar
              immediately and you get a confirmation: <em>"Added: Soccer at the park,
              tomorrow 4pm."</em>
            </li>
            <li>
              <strong>Medium confidence (something's ambiguous).</strong> The event is created
              with the ambiguous field flagged, and you get one short clarifying question:
              <em>"Got soccer Saturday — what time?"</em> Reply and we update.
            </li>
            <li>
              <strong>Low confidence (we couldn't read it).</strong> No event is created, and
              we ask for the details in plain language. We never guess.
            </li>
          </ul>
        </Section>

        <Section title="Web calendar">
          <p>
            Once you sign in, the calendar shows everything everyone in your family has texted
            in. Tap any event to see when it was added, who added it, the original message
            that created it (text, image, voice), and the confidence score. You can also add
            and edit events on the web if you'd rather type than text.
          </p>
        </Section>

        <Section title="Google Calendar sync">
          <p>
            On the Settings page, the family admin can connect a Google account so the family's
            events sync into Google Calendar — useful for everyone in your household who
            already lives in Google Calendar.
          </p>
          <p>
            Each family member can also connect <em>their own</em> Google Calendar from the
            Members page. Per-member sync is currently storing tokens (groundwork) but
            event-routing per-member is the next slice we're shipping.
          </p>
          <p>
            Outlook and Apple Calendar are on the roadmap. The architecture supports them — we
            just haven't shipped them yet.
          </p>
        </Section>

        <Section title="Privacy and SMS terms">
          <p>
            Phone numbers are used only for the SMS messages described in our{" "}
            <Link to="/sms-consent">SMS terms page</Link>. We don't sell phone numbers or share
            them with marketers. The only third parties that see your number are our SMS
            delivery providers (Twilio, Sendblue) for the express purpose of delivering the
            family's messages.
          </p>
          <p>
            You can opt out of SMS at any time by replying STOP to any Inkling message. Reply
            HELP for support, or email us at{" "}
            <a href="mailto:hello@inkling.family">hello@inkling.family</a>.
          </p>
        </Section>

        <Section title="What's still being built">
          <ul>
            <li>
              <strong>Conflict detection.</strong> Right now Inkling won't warn you if you've
              double-booked yourself. That's a Phase 2 build.
            </li>
            <li>
              <strong>Recurring events.</strong> Currently every event is one-off. "Soccer
              every Tuesday" becomes one event right now; recurrence support is Phase 2.
            </li>
            <li>
              <strong>Calendar imports.</strong> Pulling existing events <em>from</em> Google
              Calendar, Apple Calendar, and Outlook into Inkling is Phase 2 (we already do
              the push direction).
            </li>
            <li>
              <strong>Native iMessage group threads.</strong> The vision is that Inkling joins
              your existing family group chat as a participant and just listens, only acting
              when there's a clear event signal. That's the highest-value UX outcome and the
              hardest to build — Phase 3.
            </li>
            <li>
              <strong>Cross-member event confirmation.</strong> When you text in an event that
              mentions another family member ("dentist with Avery at 3pm"), we'll text Avery
              to confirm they've agreed. Logic-side groundwork is the next iteration.
            </li>
            <li>
              <strong>Multiple home locations per family.</strong> For separated families with
              two physical households, the family will be able to configure two home locations
              so location-resolution works for both. In progress.
            </li>
            <li>
              <strong>Read queries via SMS.</strong> Texting questions like "what's on
              tomorrow?" without needing to log into the web — coming.
            </li>
          </ul>
        </Section>

        <Section title="How to reach us">
          <p>
            We read everything that comes to{" "}
            <a href="mailto:hello@inkling.family">hello@inkling.family</a>. If something
            doesn't work the way it should, or the language on this page doesn't fit your
            household, please write.
          </p>
        </Section>
      </article>

      <footer className="px-6 py-10 border-t border-stone-200">
        <div className="mx-auto max-w-3xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-stone-500">
          <p>© {new Date().getFullYear()} Tall Poppy Group, LLC</p>
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-stone-900">Home</Link>
            <Link to="/sms-consent" className="hover:text-stone-900">SMS terms</Link>
            <a className="hover:text-stone-900" href="mailto:hello@inkling.family">
              hello@inkling.family
            </a>
          </nav>
        </div>
      </footer>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12 first:mt-0">
      <h2>{title}</h2>
      {children}
    </section>
  );
}
